import { deleteLoadRequests } from "apis/myRequestsService";
import React, { useState } from "react";
import { useToastStore } from "store/toastStore";

interface DeleteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  flightNo?: string;
  createdDate?: string;
  isBulkOperation?: boolean;
  requestIds?: number[];
  onDeleteSuccess?: () => void; // Add callback for successful delete
  departureDate?: string;
}

const DeleteRequestModal: React.FC<DeleteRequestModalProps> = ({
  isOpen,
  onClose,
  flightNo = "N/A",
  createdDate = "",
  isBulkOperation = false,
  requestIds = [],
  onDeleteSuccess,
  departureDate = ""
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  if (!isOpen) return null;

  // Format date for display
  const formattedDate = departureDate ? (() => {
    const date = new Date(departureDate);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  })() : "N/A";

  // Create a handler for modal interactions that stops event propagation
  const handleModalInteraction = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Wrap the onClose with stopPropagation
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError('');
    onClose();
  };

  // Handle the delete request action
  const handleDeleteRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    setError(null);

    const allIds = isBulkOperation
      ? requestIds
      : requestIds.length > 0
        ? requestIds : [0];

    const toDelete = allIds.filter((id) => !deletedIds.includes(id));

    if (toDelete.length === 0) {
      useToastStore.getState().showToast(
        isBulkOperation
          ? "All selected requests have already been deleted."
          : "Delete request already sent.",
        "info",
        3000
      );
      setIsLoading(false);
      return;
    }


    try {
      // If it's a single operation and no requestIds provided, we should have a way to get the ID


      // Assuming deleteRequests function exists in your API service
       await deleteLoadRequests(toDelete);
      setDeletedIds((prev: number[]) => [...prev, ...toDelete]);

      useToastStore.getState().showToast(
        "Delete request successful",
        "success",
        3000
      );
      
      // Call the callback to refresh the parent data
      onDeleteSuccess?.();
      
      onClose();
    } catch (err: any) {
      setError("Failed to delete request. Please try again.");
      useToastStore.getState().showToast(
        "Error deleting request",
        "error",
        3000
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id="delete-modal"
      tabIndex={-1}
      aria-hidden="true"
      className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-screen bg-black bg-opacity-50"
      onClick={handleModalInteraction}
    >
      <div className="relative p-4 w-full max-w-xl" onClick={handleModalInteraction}>
        {/* Modal content */}
        <div className="relative bg-white rounded-lg shadow" onClick={handleModalInteraction}>
          {/* Modal header */}
          <div className="flex items-center justify-between p-4 md:p-5 rounded-t">
            <h3 className="text-lg font-semibold text-grease-100">
              Delete Request
            </h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={handleClose}
              disabled={isLoading}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {/* Modal body */}
          <div className="p-4 border-t">
            {isBulkOperation ? (
              <p className="text-sm text-grease-200">
                You are about to delete{" "}
                <span className="font-semibold">{requestIds.length} selected requests</span>.
                Do you want to proceed?
              </p>
            ) : (
              <p className="text-sm text-grease-200">
                You are about to delete your request for flight{" "}
                <span className="font-semibold">{flightNo}</span> on{" "}
                <span className="font-semibold">{formattedDate}</span>. Do you want
                to proceed?
              </p>
            )}
            <div className="bg-red-50 rounded-lg p-5 flex gap-4 mt-5">
              <i className="st-info text-2xl text-red-500"></i>
              <div>
                <p className="text-sm text-grease-200">
                  {isBulkOperation
                    ? "Your tokens will not be refunded because the loads for these flights are already available."
                    : "Your token will not be refunded because the loads for this flight are already available."
                  }
                </p>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>
          <div className="flex gap-4 items-center justify-end p-4 md:p-5 border-t border-gray-200 rounded-b">
            <button
              className="flex items-center gap-2 p-3 bg-indigo-100 rounded-lg"
              onClick={handleClose}
              disabled={isLoading}
            >
              <p className="font-medium bg-skyblue-200 text-grease-50 text-sm">
                Cancel
              </p>
            </button>
            <button
              className={`flex items-center gap-2 p-3 ${isLoading ? 'bg-gray-400' : 'bg-red-500'} rounded-lg`}
              onClick={handleDeleteRequest}
              disabled={isLoading}
            >
              <p className="font-medium text-white text-sm">
                {isLoading ? "Processing..." : "Delete Request"}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteRequestModal;